import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';

import jsPDF from 'jspdf';
import {
    DataGrid,
    GridColDef,
    getGridNumericOperators,
    getGridStringOperators,
    getGridDateOperators,
    GridToolbarContainer,
    GridSlots,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
} from '@mui/x-data-grid';
import {
    ActivationStick,
    Device,
    DeviceWithSticks,
    OnboardDeviceConnectionStatus,
    UserRoles,
    VersionValidity,
} from '../interfaces';
import { transformKeyToTitle } from '../utils/transformKeyToTitle';
import { GridColType } from '@mui/x-data-grid/models/colDef/gridColType';
import Button from '@mui/material/Button';
import { UpdateVersionDialog } from './UpdateVersionDialog';
import {
    loadActivationSticks,
    loadOnboardDevices,
    loadOnboardDevicesHardwareConfigurations,
    loadOnboardDevicesOnlinePeers,
    loadSystemConfigurations,
    selectOnboardDevices,
    selectOnboardDevicesHardwareConfigurations,
    selectSelectedOnboardDeviceTableRows,
    setSelectedOnboardDeviceTableRows,
} from '../store/devices.reducer';
import RefreshIcon from '@mui/icons-material/Refresh';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import AddHomeIcon from '@mui/icons-material/AddHome';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import KeyIcon from '@mui/icons-material/Key';

import { useAppDispatch, useAppSelector } from '../store/hooks';
import { GetSecretsDialog } from './GetSecretsDialog';
import { hasAllowedRole } from '../utils/hasAllowedRole';
import { useCognitoUser } from '../hooks/useCognitoUser';
import { AttachOrganizationDialog } from './AttachOrganizationDialog';
import { CreateDevicesDialog } from './CreateDevicesDialog';
import { formatDate } from '../utils/formatDate';
import { RemoveDevicesDialog } from './RemoveDevicesDialog';
import { useManageGrid } from '../hooks/useManageGrid';
import { CopyTextCell } from './GridCells/CopyTextCell';
import { OrganizationLabelCell } from './GridCells/OrganizationLabelCell';
import { ActivationSticksCell } from './GridCells/ActivationSticksCell';
import { VersionsCell } from './GridCells/VersionsCell';
import { UpToDateCell } from './GridCells/UpToDateCell';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import { UploadOnboardDeviceLogsDialog } from './UploadOnboardDeviceLogsDialog';
import { ChecksCell } from './GridCells/ChecksCell';
import { DeviceSerialStatusCell } from './GridCells/DeviceSerialStatusCell';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

interface DataGridComponentProps {
    devices: DeviceWithSticks[];
    onlineStatusByDeviceId?: Record<string, OnboardDeviceConnectionStatus>;
}

interface ToolbarProps {
    hasScannedFilter: boolean;
    scannedDevicesCount: number;
    onResetScannedDevices: () => void;
}

const SCANNER_INPUT_RESET_TIMEOUT_MS = 100;

const isFormFieldElement = (target: EventTarget | null): target is HTMLElement => {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    const tagName = target.tagName;
    return target.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
};

export const isVersionTheSame = (collection: ActivationStick[], parentDeviceVersion: string) => {
    return collection.every((c) => c.systemVersion === parentDeviceVersion);
};

function exportToFile(devices: Device[]) {
    const formattedData = devices.map((device) => {
        return `${device.deviceSerial}\t${device.deviceId}`;
    });

    const fileContent = formattedData.join('\n');

    const blob = new Blob([fileContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Devices.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

function transformToTable(array: any[], numColumns: number) {
    const numRows = Math.ceil(array.length / numColumns);
    const table = [];

    for (let i = 0; i < numRows; i++) {
        const row = array.slice(i * numColumns, (i + 1) * numColumns);

        // If the last row is shorter than numColumns, pad it with null or a desired value
        while (row.length < numColumns) {
            row.push(null); // or another value such as 0 or an empty string
        }

        table.push(row);
    }

    return table;
}

function exportToPdf(devices: Device[]) {
    const doc = new jsPDF();
    const columns = 6;
    const rowsPerPage = 15; // Number of rows per page
    let currentPage = 1;

    const tableData = transformToTable(devices, columns);

    const addPageWithHeader = (doc: jsPDF, pageNum: number) => {
        if (pageNum > 1) {
            doc.addPage();
        }
        doc.setFontSize(11.8);
    };

    addPageWithHeader(doc, currentPage);

    let y = 23;
    let x = 10;
    const cellWidth = 31.7;
    const cellHeight = 17.5;
    let rowCount = 0;

    doc.setDrawColor(200, 200, 200);

    tableData.forEach((row) => {
        if (rowCount >= rowsPerPage) {
            currentPage++;
            addPageWithHeader(doc, currentPage);
            y = 23;
            rowCount = 0;
        }

        x = 10; // Reset x for each row
        row.forEach((cell) => {
            doc.rect(x, y - 8, cellWidth, cellHeight);
            if (cell) {
                const text = `${cell.deviceId}`;
                const textWidth = doc.getTextWidth(text);
                const textX = x + (cellWidth - textWidth - 3);
                doc.text(text, textX, y + 5.5);
            }
            x += cellWidth;
        });
        y += cellHeight;
        rowCount++;
    });

    doc.save('Devices.pdf');
}

function Toolbar({ hasScannedFilter, scannedDevicesCount, onResetScannedDevices }: ToolbarProps) {
    const dispatch = useAppDispatch();
    const { userRoles } = useCognitoUser();
    const selectedRows = useAppSelector(selectSelectedOnboardDeviceTableRows);
    const onboardDevices = useAppSelector(selectOnboardDevices);
    const [isUpdateVersionDialogOpen, setIsUpdateVersionDialogOpen] = useState(false);
    const [getSecretsDialogOpen, setGetSecretsDialogOpen] = useState(false);
    const [attachOrganizationDialogOpen, setAttachOrganizationDialogOpen] = useState(false);
    const [createDevicesDialogOpen, setCreateDevicesDialogOpen] = useState(false);
    const [removeDevicesDialogOpen, setRemoveDevicesDialogOpen] = useState(false);
    const [uploadLogsDialogOpen, setUploadLogsDialogOpen] = useState(false);

    const onVersionChange = () => {
        setIsUpdateVersionDialogOpen(true);
    };

    const onGetSecrets = () => {
        setGetSecretsDialogOpen(true);
    };

    const onAttachOrganization = () => {
        setAttachOrganizationDialogOpen(true);
    };

    const onCreateDevices = () => {
        setCreateDevicesDialogOpen(true);
    };

    const onRemoveDevices = () => {
        setRemoveDevicesDialogOpen(true);
    };

    const onReload = () => {
        dispatch(loadOnboardDevices());
        dispatch(loadActivationSticks());
        dispatch(loadSystemConfigurations());
        dispatch(loadOnboardDevicesHardwareConfigurations());
        dispatch(loadOnboardDevicesOnlinePeers());
    };

    const onUploadLogs = () => {
        setUploadLogsDialogOpen(true);
    };

    const isAdmin = useMemo(() => {
        return userRoles && hasAllowedRole(userRoles, [UserRoles.ADMINS]);
    }, [userRoles]);

    return (
        <>
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <Button
                    startIcon={<FilterAltOffIcon />}
                    disabled={!hasScannedFilter}
                    color="primary"
                    onClick={onResetScannedDevices}
                >
                    {hasScannedFilter
                        ? `Clear Scanned Devices (${scannedDevicesCount})`
                        : 'Clear Scanned Devices'}
                </Button>
                {isAdmin && (
                    <Button
                        startIcon={<UpgradeIcon />}
                        disabled={selectedRows.length === 0}
                        color="primary"
                        onClick={onVersionChange}
                    >
                        Change Version
                    </Button>
                )}
                <Button startIcon={<RefreshIcon />} color="primary" onClick={onReload}>
                    Reload
                </Button>
                <Button
                    disabled={selectedRows.length !== 1}
                    startIcon={<KeyIcon />}
                    color="primary"
                    onClick={onGetSecrets}
                >
                    Get Secrets
                </Button>
                {isAdmin && (
                    <Button
                        disabled={selectedRows.length === 0}
                        startIcon={<AddHomeIcon />}
                        color="primary"
                        onClick={onAttachOrganization}
                    >
                        Attach Organization
                    </Button>
                )}
                <Button startIcon={<AddIcon />} color="primary" onClick={onCreateDevices}>
                    Create Devices
                </Button>

                <Button
                    disabled={selectedRows.length === 0}
                    startIcon={<RemoveIcon />}
                    color="primary"
                    onClick={onRemoveDevices}
                >
                    Remove Devices
                </Button>

                <Button
                    startIcon={<AddIcon />}
                    color="primary"
                    disabled={selectedRows.length === 0}
                    onClick={() => {
                        const selectedDevices = selectedRows.map(
                            (id) => onboardDevices?.find((d) => d.deviceId == id) as Device,
                        );

                        exportToFile(selectedDevices);
                        exportToPdf(selectedDevices);
                    }}
                >
                    Export Ids
                </Button>

                <Button
                    startIcon={<StickyNote2Icon />}
                    color="primary"
                    disabled={selectedRows.length !== 1}
                    onClick={onUploadLogs}
                >
                    Upload Logs
                </Button>
            </GridToolbarContainer>

            {isUpdateVersionDialogOpen && (
                <UpdateVersionDialog
                    selectedDeviceIds={selectedRows}
                    open={isUpdateVersionDialogOpen}
                    onClose={() => setIsUpdateVersionDialogOpen(false)}
                    onUpdateComplete={() => {
                        setIsUpdateVersionDialogOpen(false);
                        dispatch(loadOnboardDevices());
                    }}
                />
            )}
            {getSecretsDialogOpen && (
                <GetSecretsDialog
                    selectedDeviceId={selectedRows[0]}
                    open={getSecretsDialogOpen}
                    onClose={() => setGetSecretsDialogOpen(false)}
                />
            )}

            {attachOrganizationDialogOpen && (
                <AttachOrganizationDialog
                    selectedDeviceIds={selectedRows}
                    open={attachOrganizationDialogOpen}
                    onClose={() => setAttachOrganizationDialogOpen(false)}
                    onAttachComplete={() => {
                        setAttachOrganizationDialogOpen(false);
                        dispatch(loadOnboardDevices());
                    }}
                />
            )}

            {createDevicesDialogOpen && (
                <CreateDevicesDialog
                    open={createDevicesDialogOpen}
                    onClose={() => setCreateDevicesDialogOpen(false)}
                    onCreationCompleted={() => {
                        setCreateDevicesDialogOpen(false);
                        dispatch(loadOnboardDevices());
                    }}
                />
            )}

            {removeDevicesDialogOpen && (
                <RemoveDevicesDialog
                    open={removeDevicesDialogOpen}
                    selectedDeviceIds={selectedRows}
                    onClose={() => setRemoveDevicesDialogOpen(false)}
                    onRemovalCompleted={() => {
                        setRemoveDevicesDialogOpen(false);
                        dispatch(loadOnboardDevices());
                    }}
                />
            )}

            {uploadLogsDialogOpen && (
                <UploadOnboardDeviceLogsDialog
                    selectedDeviceId={selectedRows[0]}
                    open={uploadLogsDialogOpen}
                    onClose={() => setUploadLogsDialogOpen(false)}
                />
            )}
        </>
    );
}

export const OnboardDevicesTableGrid = ({ devices, onlineStatusByDeviceId }: DataGridComponentProps) => {
    const dispatch = useAppDispatch();
    const {
        sortModel,
        columnsConfig,
        columnsWidth,
        gridFilterModel,
        handleColumnVisibilityChange,
        handleColumnWidthChange,
        handleSortModelChange,
        handleFilterModelChange,
        onCellKeyDown,
        gridStateSynced,
    } = useManageGrid('devices/onboard');

    const selectedRows = useAppSelector(selectSelectedOnboardDeviceTableRows);
    const hardwareConfigurations = useAppSelector(selectOnboardDevicesHardwareConfigurations);
    const [scannedDeviceIds, setScannedDeviceIds] = useState<string[]>([]);
    const scannerBufferRef = useRef('');
    const lastScannerInputAtRef = useRef<number | null>(null);

    useEffect(() => {
        dispatch(loadOnboardDevicesHardwareConfigurations());
    }, [dispatch]);

    const hwNameById = useMemo(() => {
        const map = new Map<string, string>();
        (hardwareConfigurations ?? []).forEach((c) => map.set(c.id!, c.name));

        return map;
    }, [hardwareConfigurations]);

    const deviceIdByMatrixCode = useMemo(() => {
        const map = new Map<string, string>();
        devices.forEach((device) => {
            const matrixCode = device.matrixCode?.trim();
            if (!matrixCode || !device.deviceId) {
                return;
            }

            map.set(matrixCode.toUpperCase(), device.deviceId);
        });

        return map;
    }, [devices]);

    const filteredDevices = useMemo(() => {
        if (scannedDeviceIds.length === 0) {
            return devices;
        }

        const deviceById = new Map<string, DeviceWithSticks>();
        devices.forEach((device) => {
            if (device.deviceId) {
                deviceById.set(device.deviceId, device);
            }
        });

        return scannedDeviceIds
            .map((deviceId) => deviceById.get(deviceId))
            .filter((device): device is DeviceWithSticks => Boolean(device));
    }, [devices, scannedDeviceIds]);

    const onResetScannedDevices = useCallback(() => {
        setScannedDeviceIds([]);
    }, []);

    useEffect(() => {
        const onWindowKeyDown = (event: KeyboardEvent) => {
            if (isFormFieldElement(event.target)) {
                return;
            }

            if (event.key === 'Enter') {
                const scannedCode = scannerBufferRef.current.trim();
                scannerBufferRef.current = '';
                lastScannerInputAtRef.current = null;

                if (!scannedCode) {
                    return;
                }

                const matchedDeviceId = deviceIdByMatrixCode.get(scannedCode.toUpperCase());
                if (!matchedDeviceId) {
                    return;
                }

                setScannedDeviceIds((previousIds) => {
                    if (previousIds.includes(matchedDeviceId)) {
                        return previousIds;
                    }

                    return [...previousIds, matchedDeviceId];
                });

                return;
            }

            if (event.key === 'Escape') {
                scannerBufferRef.current = '';
                lastScannerInputAtRef.current = null;
                return;
            }

            if (event.key === 'Backspace') {
                scannerBufferRef.current = scannerBufferRef.current.slice(0, -1);
                lastScannerInputAtRef.current = Date.now();
                return;
            }

            if (event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
                return;
            }

            const now = Date.now();
            // Split independent scans when there is a pause between key presses.
            if (
                lastScannerInputAtRef.current !== null &&
                now - lastScannerInputAtRef.current > SCANNER_INPUT_RESET_TIMEOUT_MS
            ) {
                scannerBufferRef.current = '';
            }

            scannerBufferRef.current += event.key;
            lastScannerInputAtRef.current = now;
        };

        window.addEventListener('keydown', onWindowKeyDown);
        return () => window.removeEventListener('keydown', onWindowKeyDown);
    }, [deviceIdByMatrixCode]);

    useEffect(() => {
        if (scannedDeviceIds.length === 0) {
            return;
        }

        const filteredIds = new Set(
            filteredDevices
                .map((device) => device.deviceId)
                .filter((deviceId): deviceId is string => Boolean(deviceId)),
        );
        const nextSelectedRows = selectedRows.filter((rowId) => filteredIds.has(rowId));

        if (nextSelectedRows.length !== selectedRows.length) {
            dispatch(setSelectedOnboardDeviceTableRows(nextSelectedRows));
        }
    }, [dispatch, filteredDevices, scannedDeviceIds.length, selectedRows]);

    const buildChecks = (checksDetails: any, calibrationFiles: any) => {
        const result: Record<string, any> = {};
        // 1) calibration files (object) -> copy as-is (day, thermal, ...)
        for (const [key, file] of Object.entries(calibrationFiles ?? {})) {
            if (!file) continue;
            result[key] = file;
        }

        // 2) checks (array) -> group into arrays by Key
        for (const chk of checksDetails ?? []) {
            const key = chk?.Key;
            if (!key) continue;

            const existing = result[key];
            if (!Array.isArray(existing)) {
                result[key] = [];
            }
            result[key].push(chk);
        }

        return result;
    };

    const columns: GridColDef[] = useMemo(() => {
        return [
            {
                key: 'deviceSerial',
                type: 'number',
                filterOperators: getGridNumericOperators,
                renderCell: DeviceSerialStatusCell,
            },
            { key: 'deviceId', renderCell: CopyTextCell },
            { key: 'organization', renderCell: OrganizationLabelCell },
            { key: 'status', renderCell: CopyTextCell },
            {
                key: 'lastOnline',
                valueFormatter: formatDate,
                filterOperators: getGridDateOperators,
            },
            { key: 'systemVersion', renderCell: VersionsCell },
            { key: 'hardwareConfiguration', renderCell: CopyTextCell },
            {
                key: 'upToDate',
                renderCell: UpToDateCell,
            },
            { key: 'checks', renderCell: ChecksCell },
            { key: 'logsS3Link', renderCell: CopyTextCell },
            {
                key: 'createdAt',
                valueFormatter: formatDate,
                filterOperators: getGridDateOperators,
            },
            {
                key: 'updatedAt',
                valueFormatter: formatDate,
                filterOperators: getGridDateOperators,
            },
            {
                key: 'activationSticksCount',
                type: 'number',
                filterOperators: getGridNumericOperators,
                renderCell: ActivationSticksCell,
            },
            { key: 'baseImageS3Link', renderCell: CopyTextCell },
            { key: 'commitHash', renderCell: CopyTextCell },
            { key: 'zymkeySerial', renderCell: CopyTextCell },
            { key: 'rpiSerial', renderCell: CopyTextCell },
            { key: 'username', renderCell: CopyTextCell },
            { key: 'hostname', renderCell: CopyTextCell },
            { key: 'sshPort', filterOperators: getGridNumericOperators, renderCell: CopyTextCell },
            { key: 'accessPointSSID', renderCell: CopyTextCell },
            { key: 'vpnProfile', renderCell: CopyTextCell },
            { key: 'vpnIPAddress', renderCell: CopyTextCell },
            { key: 'vpnPubkey', renderCell: CopyTextCell },
            { key: 'bundleDevicePubkey', renderCell: CopyTextCell },
            { key: 'bundleCloudPubkey', renderCell: CopyTextCell },
            { key: 'mapS3Link', renderCell: CopyTextCell },
            { key: 'zymkeyPubkey', renderCell: CopyTextCell },
            { key: 'deviceSSHPubkey', renderCell: CopyTextCell },
            { key: 'deviceSSHPubkeyType', renderCell: CopyTextCell },
            { key: 'deviceSSHPubkeyHash', renderCell: CopyTextCell },
        ].map((col) => {
            return {
                field: col.key,
                type: (col.type || 'string') as GridColType,
                headerName: transformKeyToTitle(col.key),
                renderCell: col.renderCell,
                valueFormatter: col.valueFormatter,
                width: columnsWidth[col.key] || 200,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                filterOperators: col.filterOperators ? col.filterOperators() : getGridStringOperators(),
            };
        });
    }, [columnsWidth]);

    const rows = useMemo(() => {
        return filteredDevices.map((device) => {
            const { checksDetails, calibrationFiles, ...deviceRest } = device;

            const row: any = {
                ...deviceRest,
                activationSticksCount: device.activationSticks.length,
                status: device.status || 'Unknown',
                id: device.deviceId,
                onlineConnectionStatus: device.deviceId
                    ? onlineStatusByDeviceId?.[device.deviceId]
                    : undefined,
            };

            row.organization = device.organizationDetails?.name || 'None';
            row.hardwareConfiguration = hwNameById.get(device.hardwareConfiguration ?? '') ?? '';
            row.lastOnline = device.vpnOnlineAt;

            row.checks = buildChecks(checksDetails, calibrationFiles);

            if (device.systemVersion && isVersionTheSame(device.activationSticks, device.systemVersion)) {
                row.upToDate = VersionValidity.VALID;
            } else {
                row.upToDate = VersionValidity.NOT_VALID;
            }

            if (row.updatedAt) row.updatedAt = new Date(row.updatedAt);
            if (row.createdAt) row.createdAt = new Date(row.createdAt);

            return row;
        });
    }, [filteredDevices, hwNameById, onlineStatusByDeviceId]);

    const onRowSelectionModelChange = (rows: any[]) => {
        dispatch(setSelectedOnboardDeviceTableRows(rows));
    };

    const ToolbarWithScannerFilter = useCallback(
        () => (
            <Toolbar
                hasScannedFilter={scannedDeviceIds.length > 0}
                scannedDevicesCount={scannedDeviceIds.length}
                onResetScannedDevices={onResetScannedDevices}
            />
        ),
        [onResetScannedDevices, scannedDeviceIds.length],
    );

    return (
        <>
            {gridStateSynced && (
                <DataGrid
                    rows={rows}
                    columns={columns}
                    slots={{
                        toolbar: ToolbarWithScannerFilter as GridSlots['toolbar'],
                    }}
                    onRowSelectionModelChange={onRowSelectionModelChange}
                    rowSelectionModel={selectedRows}
                    checkboxSelection
                    disableRowSelectionOnClick
                    onCellKeyDown={onCellKeyDown}
                    filterModel={gridFilterModel}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    onColumnWidthChange={handleColumnWidthChange}
                    onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    onFilterModelChange={handleFilterModelChange}
                    columnVisibilityModel={columnsConfig}
                />
            )}
        </>
    );
};
